import React, { useEffect, useMemo, useState } from "react";
import { Clock } from "lucide-react";
import { StatusMeter, TeamLogo } from "../bondhouse/FixtureStatus";
import { shortTeam } from "../../utils/joinedBetsHelpers";
import {
  flattenStartingSoonViews,
  formatKickOffInParts,
} from "../../utils/bettingDeskHelpers";

const ROTATE_MS = 4200;
const COUNTDOWN_MS = 1000;

const StartingSoonHeaderTicker = ({
  groups = [],
  active = false,
  onToggle,
}) => {
  const views = useMemo(() => flattenStartingSoonViews(groups), [groups]);
  const [viewIndex, setViewIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [animKey, setAnimKey] = useState(0);

  const safeIndex = views.length ? viewIndex % views.length : 0;
  const view = views[safeIndex] || null;
  const fixture = view?.fixture;
  const home = fixture?.teams?.home;
  const away = fixture?.teams?.away;

  useEffect(() => {
    setViewIndex(0);
    setAnimKey(0);
  }, [groups]);

  useEffect(() => {
    if (views.length <= 1 || paused) return undefined;
    const id = window.setInterval(() => {
      setViewIndex((i) => (i + 1) % views.length);
      setAnimKey((k) => k + 1);
    }, ROTATE_MS);
    return () => window.clearInterval(id);
  }, [views.length, paused]);

  useEffect(() => {
    const id = window.setInterval(() => setNowMs(Date.now()), COUNTDOWN_MS);
    return () => window.clearInterval(id);
  }, []);

  if (!views.length || !fixture) return null;

  const { prefix, time } = formatKickOffInParts(view.kickoffMs, nowMs);
  const label = `${prefix} ${time} · ${shortTeam(home?.name)} ${shortTeam(away?.name)}`;

  return (
    <button
      type="button"
      className={`pbd-soon-ticker${active ? " pbd-soon-ticker-on" : ""}`}
      aria-pressed={active}
      aria-label={
        active
          ? `Clear starting soon filter. Now showing ${label}`
          : `Filter starting soon fixtures. Now showing ${label}`
      }
      title={
        active
          ? "Showing kickoffs within 6 hours — tap to clear"
          : "Show fixtures kicking off within 6 hours"
      }
      onClick={onToggle}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <span className="pbd-soon-ticker-kickoff">
        <Clock size={9} strokeWidth={2.6} aria-hidden />
        <span>{prefix}</span>
        <span className="pbd-soon-ticker-kickoff-time tabular-nums">{time}</span>
      </span>

      <span key={animKey} className="pbd-soon-ticker-match pbd-soon-ticker-match-enter">
        <TeamLogo team={home} size="sm" />
        <span className="pbd-soon-ticker-team">{shortTeam(home?.name)}</span>
        <span className="pbd-soon-ticker-status">
          <StatusMeter fixture={fixture} variant="compact" />
        </span>
        <span className="pbd-soon-ticker-team">{shortTeam(away?.name)}</span>
        <TeamLogo team={away} size="sm" />
      </span>

      {views.length > 1 ? (
        <span className="pbd-soon-ticker-dots" aria-hidden>
          {views.map((v, i) => (
            <span
              key={`${v.fixture._id}-${i}`}
              className={`pbd-soon-ticker-dot${i === safeIndex ? " pbd-soon-ticker-dot-on" : ""}`}
            />
          ))}
        </span>
      ) : null}
    </button>
  );
};

export default StartingSoonHeaderTicker;
